import { useState, useEffect, useMemo } from 'react';
import { useCardOwnerships } from './useCardOwnership';
import { useOnePieceCardOwnerships } from './useOnePieceCardOwnership';
import { PokemonCard } from '../types/pokemon';
import { OnePieceCard } from '../types/onepiece';
import { enrichCardData } from '../utils/cardEnrichment';

export interface EnrichedOwnership {
  id: string; // cardId
  cardName: string;
  justtcgName?: string; // JustTCG name for One Piece cards
  justtcgId?: string; // JustTCG ID for pricing lookup
  setId?: string;
  setName: string;
  setSeries?: string;
  number: string;
  condition: string;
  language: string;
  costBasis: number;
  currentValue: number;
  purchaseDate?: string;
  location?: string;
  tags: string[];
  imageUrl: string;
  rarity?: string;
  inCollection?: boolean;
  ownershipId: string;
  quantity: number;
  game: 'pokemon' | 'onepiece'; // Track which game the card belongs to
  cardId: string; // Original card ID
  isGrading?: boolean;
  gradingCompany?: string;
  gradingGrade?: string;
  gradingId?: string;
  links?: string[];
  isFavorite?: boolean;
  isWishlist?: boolean;
  notes?: string;
}

export function useEnrichedCollection() {
  // Fetch both Pokemon and One Piece ownerships
  const {
    data: pokemonOwnerships = [],
    isLoading: pokemonLoading,
    error: pokemonError,
    refetch: refetchPokemon,
  } = useCardOwnerships(undefined, true, 'master'); // Fetch from master table (superset of all owned)

  const {
    data: onePieceOwnerships = [],
    isLoading: onePieceLoading,
    error: onePieceError,
    refetch: refetchOnePiece,
  } = useOnePieceCardOwnerships(undefined, true, 'master'); // Fetch from master table (superset of all owned)

  console.log('[useEnrichedCollection] One Piece ownerships from API:', {
    count: onePieceOwnerships.length,
    data: onePieceOwnerships,
    loading: onePieceLoading,
    error: onePieceError,
  });


  // Enrich Pokemon ownerships with card metadata using shared utility
  const [enrichedPokemon, setEnrichedPokemon] = useState<EnrichedOwnership[]>([]);
  
  useEffect(() => {
    const enrich = async () => {
      if (pokemonOwnerships.length > 0 && !pokemonLoading) {
        const enriched = await enrichCardData(pokemonOwnerships, 'pokemon');
        setEnrichedPokemon(enriched);
      } else {
        setEnrichedPokemon([]);
      }
    };
    enrich();
  }, [pokemonOwnerships, pokemonLoading]);

  // Enrich One Piece ownerships with card metadata using shared utility
  const [enrichedOnePiece, setEnrichedOnePiece] = useState<EnrichedOwnership[]>([]);
  
  useEffect(() => {
    const enrich = async () => {
      if (onePieceOwnerships.length > 0 && !onePieceLoading) {
        const enriched = await enrichCardData(onePieceOwnerships, 'onepiece');
        setEnrichedOnePiece(enriched);
      } else {
        setEnrichedOnePiece([]);
      }
    };
    enrich();
  }, [onePieceOwnerships, onePieceLoading]);

  // Combine both collections
  const enrichedCollection = useMemo(() => {
    const combined = [...enrichedPokemon, ...enrichedOnePiece];
    console.log('[useEnrichedCollection] Combining collections:');
    console.log(
      '  - enrichedPokemon:',
      enrichedPokemon.length,
      enrichedPokemon,
    );
    console.log(
      '  - enrichedOnePiece:',
      enrichedOnePiece.length,
      enrichedOnePiece,
    );
    console.log('  - combined:', combined.length, combined);
    return combined;
  }, [enrichedPokemon, enrichedOnePiece]);

  return {
    data: enrichedCollection,
    rawPokemon: pokemonOwnerships,
    rawOnePiece: onePieceOwnerships,
    loading: pokemonLoading || onePieceLoading,
    error: pokemonError || onePieceError,
    refetch: () => {
      refetchPokemon();
      refetchOnePiece();
    },
  };
}